import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkle, X, PaperPlaneTilt, BookmarkSimple, Share } from "@phosphor-icons/react";
import { streamAI, tripsApi, itinerariesApi } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AIMarkdown from "@/components/AIMarkdown";
import { useTranslation } from "react-i18next";

export default function PlannerModal({ open, onClose, onSaved }) {
    const { t } = useTranslation();
    const [destination, setDestination] = useState("Kyoto");
    const [days, setDays] = useState(5);
    const [budget, setBudget] = useState("mid");
    const [interests, setInterests] = useState("food, temples, hidden alleys");
    const [output, setOutput] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedItinerary, setSavedItinerary] = useState(null);
    const [sharedPost, setSharedPost] = useState(null);
    const outputRef = useRef(null);
    const { user } = useAuth() || {};

    const run = async () => {
        setLoading(true);
        setOutput("");
        setSavedItinerary(null);
        setSharedPost(null);
        try {
            const s = streamAI("/ai/itinerary", {
                destination,
                days: Number(days) || 5,
                budget,
                interests: interests.split(",").map((s) => s.trim()).filter(Boolean),
            });
            setTimeout(() => outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
            for await (const chunk of s) setOutput((p) => p + chunk);
        } catch (e) {
            toast.error(t("plannerModal.toasts.offline"));
        } finally { setLoading(false); }
    };

    const save = async () => {
        if (!user) return toast.error(t("plannerModal.toasts.signInToSave"));
        setSaving(true);
        try {
            await tripsApi.create({
                destination,
                days: Number(days) || 5,
                budget,
                interests: interests.split(",").map((s) => s.trim()).filter(Boolean),
                content: output,
            });
            const it = await itinerariesApi.save({
                kind: "trip",
                title: `${Number(days) || 5}-day ${destination}`,
                destination,
                days: Number(days) || 5,
                budget,
                interests: interests.split(",").map((s) => s.trim()).filter(Boolean),
                content: output,
            });
            setSavedItinerary(it);
            toast.success(t("plannerModal.toasts.saved"));
            onSaved?.();
        } catch (e) {
            toast.error(t("plannerModal.toasts.saveFailed"));
        } finally { setSaving(false); }
    };

    const shareToFeed = async () => {
        if (!user) return toast.error(t("plannerModal.toasts.signInToShare"));
        setSaving(true);
        try {
            let it = savedItinerary;
            if (!it) {
                it = await itinerariesApi.save({
                    kind: "trip",
                    title: `${Number(days) || 5}-day ${destination}`,
                    destination,
                    days: Number(days) || 5,
                    budget,
                    interests: interests.split(",").map((s) => s.trim()).filter(Boolean),
                    content: output,
                });
                setSavedItinerary(it);
            }
            const { post } = await itinerariesApi.share(it.id, { privacy: "public" });
            setSharedPost(post);
            toast.success(t("plannerModal.toasts.shared"));
        } catch { toast.error(t("plannerModal.toasts.shareFailed")); }
        finally { setSaving(false); }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[80] flex items-center justify-center p-4"
                    style={{ background: "rgba(5,10,17,0.85)", backdropFilter: "blur(8px)" }}
                    onClick={onClose}
                    data-testid="planner-modal"
                >
                    <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                        style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.3)" }}
                    >
                        <div className="p-6 md:p-8">
                            <div className="flex items-start justify-between mb-8">
                                <div>
                                    <div className="overline mb-2 flex items-center gap-2">
                                        <Sparkle size={12} weight="fill" /> {t("plannerModal.overline")}
                                    </div>
                                    <h3 className="font-serif text-3xl md:text-4xl text-white tracking-tight">{t("plannerModal.title")}</h3>
                                </div>
                                <button onClick={onClose} className="w-10 h-10 rounded-full glass grid place-items-center" data-testid="planner-close">
                                    <X size={16} />
                                </button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <Field label={t("plannerModal.labels.destination")}>
                                    <input value={destination} onChange={(e) => setDestination(e.target.value)} className="input" data-testid="planner-destination" />
                                </Field>
                                <Field label={t("plannerModal.labels.days")}>
                                    <input type="number" min="1" max="30" value={days} onChange={(e) => setDays(e.target.value)} className="input" data-testid="planner-days" />
                                </Field>
                                <Field label={t("plannerModal.labels.budget")}>
                                    <div className="flex gap-2">
                                        {["low", "mid", "luxury"].map((b) => (
                                            <button key={b} onClick={() => setBudget(b)}
                                                className="flex-1 py-3 rounded-xl text-sm capitalize transition-all duration-300"
                                                style={{
                                                    border: budget === b ? "1px solid var(--gold)" : "1px solid rgba(255,255,255,0.1)",
                                                    background: budget === b ? "rgba(212,175,55,0.1)" : "transparent",
                                                    color: budget === b ? "var(--gold)" : "white",
                                                }}
                                                data-testid={`planner-budget-${b}`}
                                            >{t(`plannerModal.budgetOptions.${b}`)}</button>
                                        ))}
                                    </div>
                                </Field>
                                <Field label={t("plannerModal.labels.interests")}>
                                    <input value={interests} onChange={(e) => setInterests(e.target.value)} className="input" data-testid="planner-interests" />
                                </Field>
                            </div>

                            <button onClick={run} disabled={loading} className="btn-gold mt-8 w-full justify-center" data-testid="planner-generate">
                                <PaperPlaneTilt size={16} weight="fill" />
                                {loading ? t("plannerModal.buttons.generating") : t("plannerModal.buttons.generate")}
                            </button>

                            {(output || loading) && (
                                <>
                                    <div ref={outputRef} className="mt-8 p-6 rounded-2xl"
                                        style={{ background: "rgba(212,175,55,0.04)", border: "1px solid rgba(212,175,55,0.2)" }}
                                        data-testid="planner-output"
                                    >
                                        {output ? (
                                            <AIMarkdown className="text-sm">{output}</AIMarkdown>
                                        ) : (
                                            <div className="text-white/70 italic">{t("plannerModal.placeholder")}</div>
                                        )}
                                    </div>
                                    {output && !loading && user && !sharedPost && (
                                        <div className="mt-4 flex gap-2">
                                            <button onClick={save} disabled={saving} className="btn-ghost flex-1 justify-center" data-testid="planner-save-trip">
                                                <BookmarkSimple size={14} weight={saving ? "fill" : "regular"} />
                                                {saving ? t("plannerModal.buttons.saving") : t("plannerModal.buttons.save")}
                                            </button>
                                            <button onClick={shareToFeed} disabled={saving} className="btn-gold flex-1 justify-center" data-testid="planner-share-trip">
                                                <Share size={14} weight="fill" />
                                                {saving ? t("plannerModal.buttons.sharing") : t("plannerModal.buttons.share")}
                                            </button>
                                        </div>
                                    )}
                                    {sharedPost && (
                                        <Link
                                            to={`/p/${sharedPost.share_slug}`}
                                            onClick={onClose}
                                            className="mt-4 w-full block py-3 rounded-full font-semibold text-sm text-center"
                                            style={{ background: "rgba(212,175,55,0.16)", color: "var(--gold)", border: "1px solid rgba(212,175,55,0.4)" }}
                                            data-testid="planner-shared-link"
                                        >
                                            {t("plannerModal.buttons.viewPost")}
                                        </Link>
                                    )}
                                </>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="overline block mb-2">{label}</span>
            {children}
            <style>{`
                .input { width: 100%; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 12px 14px; color: white; font-size: 14px; }
                .input:focus { outline: none; border-color: var(--gold); }
            `}</style>
        </label>
    );
}
